import type { ColumnDef, SortingState } from "@tanstack/react-table";
import {
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import type { ReactNode } from "react";
import { useState } from "react";
import { ClmIcon } from "@/shared/components/icons/icon-map";
import { cn } from "@/shared/utils/cn";
import { Button } from "./button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./select";

const PAGE_SIZE_OPTIONS = [5, 10, 20, 30, 40, 50];
const MAX_PAGE_BUTTONS = 6;

export interface DataGridItemLabel {
  singular: string;
  plural: string;
}

export interface DataGridProps<TData> {
  data: TData[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- ColumnDef value types vary per column, matching the tracker wrapper contract.
  columns: ColumnDef<TData, any>[];
  /**
   * Column sorting (client-side over the rendered rows). Defaults on per the
   * ux-design-guidelines. Ignored in manual-pagination mode (`totalCount`
   * provided), where client sorting would reorder only the fetched page.
   */
  enableSorting?: boolean;
  /** Pagination footer. Defaults on per the ux-design-guidelines (page size 10, options 5-50). */
  enablePagination?: boolean;
  pageSize?: number;
  /**
   * Server-driven pagination: total row count across all pages. When provided
   * the grid runs in manual-pagination mode — `data` is the current page and
   * `pageIndex`/`onPaginationChange` drive the query.
   */
  totalCount?: number;
  /** Controlled zero-based page index (server-driven pagination). */
  pageIndex?: number;
  onPaginationChange?: (pageIndex: number, pageSize: number) => void;
  /** Summary noun, e.g. { singular: "person", plural: "people" }. */
  itemLabel?: DataGridItemLabel;
  emptyMessage?: ReactNode;
  paginationLabel?: string;
  rowClassName?: string | ((row: TData, index: number) => string);
  className?: string;
}

// Ported from clm-tracker-web:
// - src/shared/components/ui/wrappers/DataGrid/DataGrid.tsx +
//   src/shared/components/ui/adapters/tanstack/DataGridAdapter.tsx (TanStack
//   table state, sorting/pagination props, page-size options, empty message),
//   extended with manual pagination so the
//   server-driven list contracts keep owning page/pageSize query params.
//   The tracker's raw tableOptions passthrough is intentionally NOT ported:
//   it let callers bypass the server-paginated sorting guard (CR41-F3) and
//   had no consumers. Add narrowly-scoped props if extension is needed.
// - src/shared/components/ui/adapters/shadcn/DataTableAdapter.tsx (branded
//   markup: gray-50 header, zebra rows, hover, numbered pagination with the
//   active clm-primary-blue page and chevron prev/next). The hard-coded
//   "subscriber(s)" summary is parameterized via itemLabel per the guidelines.
export function DataGrid<TData>({
  data,
  columns,
  enableSorting = true,
  enablePagination = true,
  pageSize = 10,
  totalCount,
  pageIndex,
  onPaginationChange,
  itemLabel = { singular: "item", plural: "items" },
  emptyMessage = "No data available",
  paginationLabel = "Pagination",
  rowClassName,
  className,
}: DataGridProps<TData>) {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [internalPagination, setInternalPagination] = useState({ pageIndex: 0, pageSize });

  const isServerPaginated = totalCount !== undefined;
  // Server-paginated data holds only the current page, so client sorting is
  // disabled to avoid implying full-result ordering (CR41-F3).
  const sortingEnabled = enableSorting && !isServerPaginated;
  const pagination = isServerPaginated
    ? { pageIndex: pageIndex ?? 0, pageSize }
    : internalPagination;
  const total = isServerPaginated ? totalCount : data.length;
  const pageCount = enablePagination ? Math.max(1, Math.ceil(total / pagination.pageSize)) : 1;

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    ...(enablePagination && isServerPaginated ? { manualPagination: true, pageCount } : {}),
    ...(enablePagination && !isServerPaginated
      ? { getPaginationRowModel: getPaginationRowModel() }
      : {}),
    // Server-paginated mode disables client sorting so a single fetched page
    // never implies full-result ordering (CR41-F3). DataGrid fully owns
    // sorting — there is deliberately no raw table-options passthrough.
    enableSorting: sortingEnabled,
    ...(sortingEnabled
      ? { getSortedRowModel: getSortedRowModel(), onSortingChange: setSorting }
      : {}),
    state: {
      ...(enablePagination ? { pagination } : {}),
      sorting: sortingEnabled ? sorting : [],
    },
  });

  const rows = table.getRowModel().rows;
  const goToPage = (nextPageIndex: number) => {
    if (isServerPaginated) {
      onPaginationChange?.(nextPageIndex, pagination.pageSize);
    } else {
      setInternalPagination((current) => ({ ...current, pageIndex: nextPageIndex }));
      onPaginationChange?.(nextPageIndex, pagination.pageSize);
    }
  };
  const changePageSize = (nextPageSize: number) => {
    if (isServerPaginated) {
      onPaginationChange?.(0, nextPageSize);
    } else {
      setInternalPagination({ pageIndex: 0, pageSize: nextPageSize });
      onPaginationChange?.(0, nextPageSize);
    }
  };

  const currentPage = pagination.pageIndex + 1;
  const rangeStart = total === 0 ? 0 : pagination.pageIndex * pagination.pageSize + 1;
  const rangeEnd = pagination.pageIndex * pagination.pageSize + rows.length;
  const noun = total === 1 ? itemLabel.singular : itemLabel.plural;

  return (
    <div className={cn("w-full", className)}>
      <div className="rounded-md border border-clm-light-gray">
        {/* `relative` makes this scroll container the containing block for
            absolutely positioned descendants (e.g. Tailwind `sr-only` column
            headers). Without it they escape the overflow clip at their static
            table position and stretch the page horizontally on narrow
            viewports (CLMS-124 live-validation defect LV-4). */}
        <div className="relative overflow-x-auto">
          <table className="w-full">
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id} className="border-b border-clm-light-gray bg-gray-50">
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      scope="col"
                      aria-sort={
                        header.column.getIsSorted() === "asc"
                          ? "ascending"
                          : header.column.getIsSorted() === "desc"
                            ? "descending"
                            : undefined
                      }
                      className="p-4 text-left text-sm font-semibold text-clm-dark-gray"
                    >
                      {header.isPlaceholder ? null : header.column.getCanSort() ? (
                        <button
                          type="button"
                          onClick={header.column.getToggleSortingHandler()}
                          className="flex cursor-pointer select-none items-center gap-2 hover:text-clm-primary-blue"
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          <ClmIcon name="sort" decorative className="h-4 w-4 opacity-50" />
                        </button>
                      ) : (
                        flexRender(header.column.columnDef.header, header.getContext())
                      )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {rows.length ? (
                rows.map((row, index) => (
                  <tr
                    key={row.id}
                    className={cn(
                      "border-b border-clm-light-gray transition-colors hover:bg-gray-100",
                      index % 2 === 0 ? "bg-white" : "bg-gray-50",
                      typeof rowClassName === "function"
                        ? rowClassName(row.original, index)
                        : rowClassName,
                    )}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id} className="p-4 text-sm text-clm-dark-gray">
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={columns.length} className="h-24 text-center text-clm-dark-gray">
                    {emptyMessage}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      {enablePagination ? (
        <nav
          aria-label={paginationLabel}
          className="flex flex-wrap items-center justify-between gap-4 border-t border-clm-light-gray px-2 py-4"
        >
          <p className="text-sm text-clm-dark-gray">
            Showing {rangeStart}–{rangeEnd} of {total} {noun}
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => goToPage(pagination.pageIndex - 1)}
              disabled={currentPage <= 1}
              aria-label="Go to previous page"
              className="h-10 w-10 p-0"
            >
              <ClmIcon name="chevron-left" decorative className="h-5 w-5" />
            </Button>
            <div className="flex items-center gap-1">
              {Array.from({ length: Math.min(MAX_PAGE_BUTTONS, pageCount) }, (_, i) => {
                let pageNumber: number;
                if (pageCount <= MAX_PAGE_BUTTONS) {
                  pageNumber = i + 1;
                } else if (currentPage <= 3) {
                  pageNumber = i + 1;
                } else if (currentPage >= pageCount - 2) {
                  pageNumber = pageCount - (MAX_PAGE_BUTTONS - 1) + i;
                } else {
                  pageNumber = currentPage - 2 + i;
                }
                const isActive = currentPage === pageNumber;
                return (
                  <Button
                    key={pageNumber}
                    variant={isActive ? "default" : "ghost"}
                    size="sm"
                    onClick={() => goToPage(pageNumber - 1)}
                    aria-label={`Go to page ${pageNumber}`}
                    aria-current={isActive ? "page" : undefined}
                    className={cn(
                      "h-10 w-10 p-0",
                      isActive
                        ? "bg-clm-primary-blue text-white hover:bg-clm-primary-blue"
                        : "text-clm-primary-blue",
                    )}
                  >
                    {pageNumber}
                  </Button>
                );
              })}
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => goToPage(pagination.pageIndex + 1)}
              disabled={currentPage >= pageCount}
              aria-label="Go to next page"
              className="h-10 w-10 p-0"
            >
              <ClmIcon name="chevron-right" decorative className="h-5 w-5" />
            </Button>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-clm-dark-gray">Rows per page</span>
            <Select
              value={String(pagination.pageSize)}
              onValueChange={(value) => changePageSize(Number(value))}
            >
              <SelectTrigger aria-label="Rows per page" className="h-10 w-[4.5rem]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PAGE_SIZE_OPTIONS.map((option) => (
                  <SelectItem key={option} value={String(option)}>
                    {option}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </nav>
      ) : null}
    </div>
  );
}
